// =============================================================================
// Allocation classes (the TPA model, matching EBG RPC and every comparable
// proposal system): a class is a free-text code written on the CENSUS ROW
// ("A", "B", "Owner", "Class 3"). The plan stores one formula per class.
//
// The class LIST is derived, never authored. It is the distinct set of codes
// present in the census, unioned with any class an existing plan formula still
// references, so loading a plan against a different census never silently drops
// a formula row. Rows with no code fall into one trailing catch-all.
//
// This is deliberately NOT a rules engine. Class membership is an INPUT that is
// fixed before testing runs; deriving it from computed HCE status would make a
// person's class move when their prior-year compensation or the plan year
// changes, which no plan document can express.
// =============================================================================

// This module owns the class primitives and imports nothing, so planDesign.js
// can depend on it without a cycle. The catch-all keeps its historical id and
// label so plan formulas authored under the previous group model keep matching
// after migration.
export const EVERYONE_ELSE_GROUP_ID = 'group-everyone-else'
export const EVERYONE_ELSE_LABEL = 'Everyone else'
export const UNCLASSED_CLASS_ID = EVERYONE_ELSE_GROUP_ID
export const UNCLASSED_CLASS_LABEL = EVERYONE_ELSE_LABEL

const RESOLUTION_SOURCES = ['profitSharing', 'match', 'cashBalance']

// Label → slug. Kept identical to the retired group editor's slugifier so a
// census code migrated from a group label produces the same class id, and every
// plan formula authored before the collapse still resolves.
export function slugifyGroupId(label, taken = new Set()) {
  const base = `group-${String(label || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'unnamed'}`
  if (!taken.has(base)) return base
  let suffix = 2
  while (taken.has(`${base}-${suffix}`)) suffix += 1
  return `${base}-${suffix}`
}

export const readClassLabel = (employee) => String(employee?.allocation_group || '').trim()

// Label → stable id. Never takes a `taken` set: one label must always map to
// the same id, so census text and plan formula keys stay in agreement. Labels
// that differ only in case or punctuation intentionally collapse to one class.
export function classIdForLabel(label) {
  const trimmed = String(label || '').trim()
  return trimmed ? slugifyGroupId(trimmed) : UNCLASSED_CLASS_ID
}

// Distinct census codes in first-appearance order, then any class a plan
// formula still points at, then the mandatory trailing catch-all.
export function deriveAllocationClasses(employees = [], referencedClasses = []) {
  const byId = new Map()
  for (const employee of Array.isArray(employees) ? employees : []) {
    const label = readClassLabel(employee)
    if (!label) continue
    const id = classIdForLabel(label)
    if (id === UNCLASSED_CLASS_ID || byId.has(id)) continue
    byId.set(id, { id, label, source: 'census' })
  }
  for (const entry of Array.isArray(referencedClasses) ? referencedClasses : []) {
    const id = String(entry?.id || '').trim()
    if (!id || id === UNCLASSED_CLASS_ID || byId.has(id)) continue
    // A formula whose class nobody in the census carries. Kept visible and
    // flagged rather than dropped, so loading a plan file cannot lose a rate.
    byId.set(id, { id, label: String(entry.label || id), source: 'plan' })
  }
  return [
    ...byId.values(),
    { id: UNCLASSED_CLASS_ID, label: UNCLASSED_CLASS_LABEL, source: 'catch-all' },
  ]
}

// Runtime membership. Pure, and the return shape is identical to what the
// engine consumed under the old group model, so calculatePlanResults and the
// profit-sharing/match/cash-balance components need no changes.
export function resolveClassMembership(employees = []) {
  const resolution = {}
  for (const employee of Array.isArray(employees) ? employees : []) {
    if (!employee?.id) continue
    const label = readClassLabel(employee)
    const classId = classIdForLabel(label)
    const origin = label ? 'class' : 'unclassed'
    const resolved = { origins: {} }
    for (const source of RESOLUTION_SOURCES) {
      resolved[source] = classId
      resolved.origins[source] = origin
    }
    resolution[employee.id] = resolved
  }
  return resolution
}

// Pure census projections shared by the store writers and the demo-cap guard,
// so the rows the guard checks are exactly the rows that get written.
export function projectClassAssignment(employees = [], employeeIds = [], label) {
  const ids = new Set((Array.isArray(employeeIds) ? employeeIds : []).filter(Boolean))
  const next = String(label ?? '').trim()
  return (Array.isArray(employees) ? employees : []).map((employee) => (
    ids.has(employee.id) && readClassLabel(employee) !== next
      ? { ...employee, allocation_group: next }
      : employee
  ))
}

// "Each participant in their own class": every selected row gets a distinct
// code (their name, falling back to their ID). Two people can share a name, so
// collisions are disambiguated or they would silently land in one class.
export function projectIndividualClasses(employees = [], employeeIds = []) {
  const ids = new Set((Array.isArray(employeeIds) ? employeeIds : []).filter(Boolean))
  const taken = new Set()
  return (Array.isArray(employees) ? employees : []).map((employee) => {
    if (!ids.has(employee.id)) return employee
    let code = String(employee.name || '').trim() || employee.id
    if (taken.has(code.toLowerCase())) code = `${code} (${employee.id})`
    taken.add(code.toLowerCase())
    return { ...employee, allocation_group: code }
  })
}

export function classLabelById(classes = [], classId) {
  const entry = (classes || []).find((candidate) => candidate.id === classId)
  if (!entry) return null
  return entry.id === UNCLASSED_CLASS_ID ? '' : entry.label
}

// How many census rows carry each class, keyed by class id.
export function countClassMembers(employees = [], classes = []) {
  const counts = Object.fromEntries((classes || []).map((entry) => [entry.id, 0]))
  for (const employee of Array.isArray(employees) ? employees : []) {
    const id = classIdForLabel(readClassLabel(employee))
    if (counts[id] !== undefined) counts[id] += 1
  }
  return counts
}
